import _ from "lodash";
import L from "lodash/fp";

export const getTitle = mediaModel => {
  const mediaType = mediaModel.get("mediaType");

  if (mediaType === "anthology" || mediaType === "collection" || mediaType === "module") {
    return mediaModel.get("name");
  } else if (mediaType === "content" || mediaType === "curation") {
    return mediaModel.get("title");
  } else {
    throw new Error(`Cannot determine title for curation media tile, media type: ${mediaType}`);
  }
};

export const getThumbnailUrl = mediaModel => {
  const mediaType = mediaModel.get("mediaType");

  if (mediaType === "anthology" || mediaType === "collection" || mediaType === "module") {
    return mediaModel.get("iconUrl");
  } else if (mediaType === "content" || mediaType === "curation") {
    if (mediaModel.get("type") === "AUDIO") {
      return "";
    } else {
      return mediaModel.get("thumbnailUrl");
    }
  } else {
    throw new Error(`Cannot determine thumbnail for curation media tile, media type: ${mediaType}`);
  }
};

export const getDetailUrl = mediaModel => {
  const mediaType = mediaModel.get("mediaType");
  const mediaId = mediaModel.get("internalCode") ?? mediaModel.get("id");

  if (mediaType === "content") {
    return `/content/${mediaId}`;
  } else if (mediaType === "anthology") {
    const typeLabel = mediaModel.get("typeLabel").toLowerCase();
    return `/${typeLabel}/${mediaId}`;
  } else if (mediaType === "collection") {
    if (_.has(mediaModel, "anthology")) {
      const anthologyTypeLabel = mediaModel.get("anthology").typeLabel.toLowerCase();
      const collectionTypeLabel = mediaModel.get("typeLabel").toLowerCase();
      const anthologyId = mediaModel.get("anthology").id;
      return `/${anthologyTypeLabel}-${collectionTypeLabel}/${anthologyId}/${mediaId}`;
    } else {
      return `/collection/${mediaId}`;
    }
  } else if (mediaType === "module") {
    const internalCode = mediaModel.get("internalCode");
    return `/module/${internalCode}`;
  } else if (mediaType === "curation") {
    return `/curation/${mediaId}`;
  } else {
    throw new Error(`Cannot determine detail URL for curation media tile, media type: ${mediaType}`);
  }
};

export const getHighestQualityVideoUrl = videoFormats => {
  return _.chain(videoFormats)
    .sortBy(format => Number.parseInt(format.resolution.replace("p", "")))
    .last()
    .get("url")
    .value();
};

export const getDownloadUrl = mediaModel => {
  const mimeType = mediaModel.get("mimeType");
  const contentType = mediaModel.get("type");

  let downloadUrl;

  if (contentType === "IMAGE") {
    downloadUrl = mediaModel.get("imageUrl");
  } else if (mimeType === "image/jpeg" || mimeType === "application/pdf") {
    downloadUrl = mediaModel.get("pdfUrl");
  } else if (mimeType === "application/epub+zip") {
    downloadUrl = mediaModel.get("epubFileUrl");
  } else if (contentType === "VIDEO") {
    const formats = mediaModel.get("videoFormats");
    const videoProvider = mediaModel.get("videoProvider");
    // if video format is provided
    if (!_.isEmpty(formats) && videoProvider === "INTERNAL_AWS_CREATOR") {
      return getHighestQualityVideoUrl(formats);
    } else {
      return mediaModel.get("viewUrl");
    }
  }

  if (typeof downloadUrl === "string") {
    return downloadUrl.replace("content", "external_content");
  } else {
    return null;
  }
};

export const getContentCount = (contentType, countModel) => {
  switch (contentType) {
    case "all-media":
      return countModel.allMediaCount;
    case "all-content":
      return countModel.allContentCount;
    case "articles":
      return countModel.articleCount;
    case "audio":
      return countModel.audioCount;
    case "audiobook":
      return countModel.audiobookCount;
    case "books":
      return countModel.bookCount;
    case "videos":
      return countModel.videoCount;
    case "images":
      return countModel.imageCount;
    case "other-documents":
      return countModel.otherDocumentCount;
    case "highlights":
      return countModel.contentHighlightCount;
    case "modules":
      return countModel.moduleCount;
    case "anthologies":
      return countModel.anthologyCount;
    case "textbooks":
      return countModel.textbookCount;
    case "collections":
      return countModel.collectionCount;
    case "chapters":
      return countModel.chapterCount;
    default:
      return countModel.allMediaCount;
  }
};

export const getType = model => {
  const mediaType = model.get("mediaType");

  if (mediaType === "content") {
    const contentType = model.get("type");

    if (contentType === "OTHER_DOCUMENT") {
      return "DOCUMENT";
    } else {
      return contentType;
    }
  } else if (mediaType !== "content") {
    return mediaType.toUpperCase();
  } else {
    throw new Error(`Cannot associate media type with curation media tile, media type: ${mediaType}`);
  }
};

export const getItemType = model => {
  const type = model.get("type");

  switch (type) {
    case "ARTICLE":
      return "Periodical";
    case "IMAGE":
      return "ImageObject";
    case "VIDEO":
      return "VideoObject";
    case "AUDIO":
      return "AudioObject";
    default:
      return "Book";
  }
};

export const getIcon = model => {
  const type = model.get("type");

  switch (type) {
    case "ALBUM":
      return "fa-solid fa-album";
    case "ARTICLE":
      return "bbico bbico-article";
    case "AUDIO":
    case "AUDIOBOOK":
      return "bbico bbico-audio";
    case "DOCUMENT":
      return "bbico bbico-document";
    case "IMAGE":
      return "bbico bbico-photo";
    case "VIDEO":
      return "bbico bbico-video";
    default:
      return "bbico bbico-book";
  }
};

export const getAnthologies = L.filter(["mediaType", "anthology"]);
export const getCollections = L.filter(["mediaType", "collection"]);

// TODO: Temporary solution for adding michigan read logo to content details page; remove at a later point
// UPDATE: 11/6/2023 [Tyler] - No longer used. Leaving in case logo/badge needs to be added to these media IDs in the future.
export const isGreatMichiganRead = mediaId => {
  const greatMichiganReadIds = ["777e29e1-e162-4a68-a059-f7399875280e", "eedee105-0514-4dd3-9721-83866c3f6f89"];
  return _.includes(greatMichiganReadIds, mediaId);
};

// TODO: Temporary solution to only show Local Library for IAP content.
//       These are all the modules which contain IAP content.
export const IAP_STATE_MODULES = [
  "c31fbd5e-4542-4e6c-b9ea-8bcc92f1935b",
  "131d4907-6460-4501-8dee-6e52e57e404e",
  "821f2a4a-153d-4519-82dc-0117895530d4",
  "5f87f519-46f5-4f5b-9046-b27f99701035",
  "52cc2249-6389-4d3d-9331-fb4e351ed3da",
  "a03d97d1-87e0-43b5-a80f-add8ed9d2b4c",
  "78dd9e22-ffaf-431c-baa2-afc3aae57228",
  "5b1b3765-68bf-4f31-8b20-284a86ddbbb2",
  "4f5f6cc8-74b9-46a5-80fe-451bb0cbf3e9",
  "859c586e-caf1-4fc9-8954-2ae6f7cda855",
  "4578ed72-3009-4126-81a6-fe971f1ad165",
  "b9d17835-85ed-44b0-8adc-de1631eb7092",
  "84ed54af-2888-421d-9dc1-f6202e1efcec",
  "acca11b0-ce08-47d5-8e9e-61f2a36b783c",
  "6e527d8e-9085-40c1-9c16-043a8d491797",
  "797be358-03aa-4e1a-b6bc-492388f8eed4",
  "bd46d1fd-addb-4835-8ed5-7cd26eee3305",
  "51c91128-0158-46a3-980f-eb70e0c8bd9b",
  "13d03119-1371-4102-9f21-cb2cfdbdc140",
  "529efe43-b32b-4c8f-b782-ecfcdb94c2f5",
  "8d1a69bc-15ec-47ab-9d47-e62c9efd6839",
  "4d2837f5-33f5-431f-9402-0d27aaa0ee02",
  "5f7daed6-01ef-4957-9442-bf04048060b1",
  "0fc3886c-dc7d-4980-b97e-20f7a9bbd9d1",
  "60c78ae7-2938-424b-a047-73803716fb0e",
  "1e487c6d-1bdc-49e8-927b-f0e02343330e",
  "357bd31f-b6de-4c28-9b82-e080bdb19f8d",
  "a152ea6b-4f72-4db5-9905-ac97103bd4b4",
  "467084cc-f5f8-42c2-9861-e4fc1be8fdb5",
  "68be3611-f8a6-430c-bdfb-eb7455ff92e7",
  "00ad7551-7145-4398-8abe-819bf5ae648a",
  "11f8fdcd-56f8-476c-9e77-7ea2cd21016a",
  "8aaa027a-abac-49a3-8b28-8b64c8d4a694",
  "160dedd4-84df-4ab0-9575-865942fcc786",
  "8242e21b-b1c6-47a5-8026-4b9e286a8ade",
  "36d43f0f-37c1-4f35-95c3-2a759d38061d",
  "097c7583-0cff-4572-ad41-36162f6bc261",
  "48c97dfd-490f-4986-9955-6d2562d55bf7",
  "4640e000-5296-410c-85e9-a15e4db396b1",
  "e64ff60b-95c0-423a-9e18-83bfdf152ea4",
  "3a2e1f74-7ce7-4c17-9843-7081883f379e",
  "47c39df1-3b16-4df6-99a8-7b4c3522102d",
  "fa88fbe2-8fa9-407f-96f0-fb33d7e54f0c",
  "4128df79-fdfa-453a-b84c-d2b8ff46ac18",
  "e99c9a1d-861e-43ed-93a6-60275fddfd9a",
  "61897181-3952-419a-b41e-6acf55a5d90d",
  "3c774d3a-940f-41d6-a273-a270ee9cbbd2",
  "ad85db0e-031d-41f1-be52-a8e0136e3b49",
  "1f7195a8-8220-4cd2-b4db-07d9b1a41a53",
  "460b6776-96f8-421e-ab04-b224f1ae5535",
  "e2b9c008-7297-4e03-b482-bc79ab02eec1",
  "72688476-ceee-4c83-a465-1a566bfab393",
  "e74fbce7-f8dd-4799-9f25-9d207682e3e8",
  "84bbecc7-fbc0-47d9-97ff-f43e7aa14466",
  "b62bf5ff-5b02-4b1c-a897-ae17b6131c9d",
  "85b90f7b-fa53-4614-a54a-72c7eaba7a1e",
  "1339f6dd-3f5e-42f0-b14f-5ac9cc5a913c",
  "5fd312ec-b2d3-4fd8-9482-bc0f9a8c50af",
  "928e79d2-8c92-43bc-b10d-ce91b12aeaa9",
  "bd0fd3b3-d0de-4e99-b6f6-8cff92b2a8eb",
  "ce660a1b-0f35-446d-9e46-3542c1b17de5",
  "bbc0e67f-b2e5-4dab-97dc-68c75fd030b9",
  "3b1955e1-1c75-4fdd-9c61-f8de4f049333",
  "0b7b18c7-5aaa-44c7-8e7c-fe9864239fa8",
  "536fb9a1-acaa-4f69-b27f-330f0ba659cf",
];

const isInIapModule = media => L.some(module => L.includes(module.id, IAP_STATE_MODULES), media.ancestorModules);
export const isIap = content => content.iapSelect || isInIapModule(content);
